import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  input,
  output,
  inject,
} from '@angular/core';
import { AsyncPipe, LowerCasePipe, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG Imports
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { PaginatorModule } from 'primeng/paginator';

// Shared Components
import { StaffInfoComponent } from '@fitness-force/shared/staff-exports';

import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { AuditTrailService } from '../../services/audit-trail.service';
import {
  AuditTrailEvent,
  AuditTrailFilters,
} from '../../models/audit-trail.models';
import { StaffFieldMatcher } from '../../utils/staff-field-matcher.util';

// Store integration
import { Store } from '@ngrx/store';
import { DayAgoPipe } from '@fitness-force/shared-utils';
import { TimeconversionService } from '@fitness-force/api';
import { StatusEnumUtil } from '../../utils/status-enum.util';

@Component({
  selector: 'fitness-force-audit-trail-mini',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    TagModule,
    TooltipModule,
    SkeletonModule,
    CardModule,
    ChipModule,
    PaginatorModule,
    StaffInfoComponent,
    DayAgoPipe,
    AsyncPipe,
    LowerCasePipe,
    TitleCasePipe,
  ],
  templateUrl: './audit-trail-mini.component.html',
  styleUrls: ['./audit-trail-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditTrailMiniComponent implements OnInit, OnDestroy, OnChanges {
  private auditTrailService = inject(AuditTrailService);
  private store = inject(Store);
  private timeService = inject(TimeconversionService);
  private cd = inject(ChangeDetectorRef);

  readonly entityType = input<string>('');
  readonly entityId = input<string>('');
  readonly tenantId = input<number>();
  readonly maxEvents = input<number>(10);
  readonly showFilters = input<boolean>(true);
  readonly showStats = input<boolean>(true);
  readonly showPagination = input<boolean>(true);
  readonly showCloseButton = input<boolean>(false);
  readonly height = input<string>('400px');
  readonly title = input<string>('Activity History');
  readonly tenantData = input<any[]>([]); // Inject tenant data from parent component

  readonly closeEvent = output<void>();

  private destroy$ = new Subject<void>();

  // Data subjects
  private eventsSubject = new BehaviorSubject<AuditTrailEvent[]>([]);
  private filtersSubject = new BehaviorSubject<AuditTrailFilters>({
    operation: '',
    pageNumber: 1,
    pageSize: 10,
  });
  private isInitialized = false;

  // Public observables
  events$ = this.eventsSubject.asObservable();
  filters$ = this.filtersSubject.asObservable();

  // Filters
  selectedOperation = '';
  isCollapsed = false;

  // Dropdown options
  operationOptions = [
    { label: 'All Operations', value: '', icon: 'pi pi-list' },
    { label: 'Create', value: 'CREATE', icon: 'pi pi-plus-circle' },
    { label: 'Update', value: 'UPDATE', icon: 'pi pi-pencil' },
    { label: 'Delete', value: 'DELETE', icon: 'pi pi-trash' },
  ];

  allTenants: any[] = [];

  // Statistics
  totalEvents = 0;
  totalCreates = 0;
  totalUpdates = 0;
  totalDeletes = 0;

  // Pagination
  totalRecords = 0;
  currentPage = 1;
  pageSize = 10;
  first = 0;

  loading$: any;

  constructor() {
    this.loading$ = this.auditTrailService.loading$;
  }

  ngOnInit(): void {
    // Use tenant data provided through input
    this.allTenants = this.tenantData();

    // Initialize pagination
    this.pageSize = this.maxEvents();

    this.setupDataSubscriptions();
    this.isInitialized = true;

    // Trigger initial data load through filter subscription
    if (this.entityType()) {
      const initialFilters = this.prepareFilters();
      this.filtersSubject.next(initialFilters);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update tenant data if it changes
    if (changes['tenantData']) {
      this.allTenants = this.tenantData();
    }

    // Reload data when input properties change (only after initialization)
    if (
      this.isInitialized &&
      (changes['entityType'] || changes['entityId'] || changes['tenantId']) &&
      this.entityType()
    ) {
      this.loadInitialData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInitialData(): void {
    if (!this.entityType()) {
      console.warn('AuditTrailMiniComponent: entityType is required');
      return;
    }

    const initialFilters = this.prepareFilters();
    this.filtersSubject.next(initialFilters);
  }

  private setupDataSubscriptions(): void {
    // Subscribe to filter changes to reload data
    this.filters$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((filters) => {
        // Only load if component is initialized and we have a valid entity type
        if (this.isInitialized && this.entityType()) {
          this.loadAuditTrails(filters);
        }
      });
  }

  private prepareFilters(): AuditTrailFilters {
    const filters: AuditTrailFilters = {
      entityType: this.entityType(),
      entityId: this.entityId(),
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      operation: this.selectedOperation || '',
    };

    const tenantId = this.tenantId();
    if (tenantId) {
      filters.tenantId = tenantId;
    }

    return filters;
  }

  private loadAuditTrails(filters: AuditTrailFilters): void {
    const entityType = this.entityType();
    const entityId = this.entityId();
    if (!entityType) return;

    this.auditTrailService
      .getAuditTrails(entityType, entityId, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.eventsSubject.next(response.events);
          this.totalRecords = response.total_items;
          this.calculateStatistics(response.events);
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error('Error loading audit trails:', error);
          this.eventsSubject.next([]);
          this.totalRecords = 0;
          this.resetStatistics();
        },
      });
  }

  private calculateStatistics(events: AuditTrailEvent[]): void {
    this.totalEvents = events.length;
    this.totalCreates = events.filter((e) => e.operation === 'CREATE').length;
    this.totalUpdates = events.filter((e) => e.operation === 'UPDATE').length;
    this.totalDeletes = events.filter((e) => e.operation === 'DELETE').length;
  }

  private resetStatistics(): void {
    this.totalEvents = 0;
    this.totalCreates = 0;
    this.totalUpdates = 0;
    this.totalDeletes = 0;
  }

  onOperationChange(): void {
    if (this.isInitialized) {
      // Reset pagination when filter changes
      this.currentPage = 1;
      this.first = 0;
      const filters = this.prepareFilters();
      this.filtersSubject.next(filters);
    }
  }

  onRefresh(): void {
    if (this.isInitialized) {
      const filters = this.prepareFilters();
      this.filtersSubject.next(filters);
    }
  }

  clearFilters(): void {
    this.selectedOperation = '';
    this.onOperationChange();
  }

  onPageChange(event: any): void {
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.pageSize = event.rows;
    this.first = event.first;

    if (this.isInitialized && this.entityType()) {
      const filters = this.prepareFilters();
      this.loadAuditTrails(filters);
    }
  }

  get hasActiveFilters(): boolean {
    return this.selectedOperation !== '';
  }

  closeComponent(): void {
    this.closeEvent.emit();
  }

  get currentEvents$(): Observable<AuditTrailEvent[]> {
    return this.events$;
  }

  // Template helper methods
  getOperationConfig(operation: string) {
    const configs = {
      CREATE: {
        icon: 'pi pi-plus-circle',
        color: '#22c55e',
        bgColor: '#dcfce7',
        symbol: '+',
      },
      UPDATE: {
        icon: 'pi pi-pencil',
        color: '#3b82f6',
        bgColor: '#dbeafe',
        symbol: '~',
      },
      DELETE: {
        icon: 'pi pi-trash',
        color: '#ef4444',
        bgColor: '#fee2e2',
        symbol: '×',
      },
    };
    return configs[operation as keyof typeof configs] || configs.UPDATE;
  }

  getTimeAgo(timestamp: string): string {
    const now = new Date();
    const eventTime = new Date(timestamp);
    const diffInMinutes = Math.floor(
      (now.getTime() - eventTime.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  }

  generateEventStatement(event: any) {
    const fieldName = this.getEntityFieldName(event.fields);

    switch (event.operation) {
      case 'CREATE':
        return {
          action: 'Created',
          preposition: 'With',
          field: fieldName,
          hasValueChange: true,
          showOldValue: false,
          showNewValue: true,
        };

      case 'DELETE':
        return {
          action: 'Deleted',
          preposition: null,
          field: null,
          hasValueChange: false,
          showOldValue: false,
          showNewValue: false,
        };

      case 'UPDATE':
        return {
          action: 'Updated',
          preposition: 'From',
          field: fieldName,
          hasValueChange: true,
          showOldValue: true,
          showNewValue: true,
        };

      default:
        return {
          action: 'Modified',
          preposition: null,
          field: fieldName,
          hasValueChange: false,
          showOldValue: false,
          showNewValue: false,
        };
    }
  }

  getEntityFieldName(fields: any): string {
    // If fields is a string like "entity_field_one_field_two", remove the entity prefix (before first '_')
    // Then, split remaining by '_', join with space, and convert to Title Case
    if (typeof fields !== 'string') return '';
    // Remove prefix before first '_'
    const withoutPrefix = fields.includes('_')
      ? fields.substring(fields.indexOf('_') + 1)
      : fields;
    // Replace underscores with spaces
    const withSpaces = withoutPrefix.replace(/_/g, ' ');
    // Convert to Title Case
    return withSpaces.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    );
  }

  getTenantName(tenantId: number): string {
    const tenant = this.allTenants.find((t: any) => t.id === tenantId);
    return tenant
      ? tenant.name_primary_language || tenant.name_secondary_language
      : '';
  }

  getAppSourceLabel(trackMode: number): string {
    const sources: Record<number, string> = {
      1: 'Staff App',
      2: 'Admin Panel',
      3: 'Mobile App',
      4: 'API',
      8: 'Staff App',
    };
    return sources[trackMode] || 'Web App';
  }

  trackByEventId(index: number, event: AuditTrailEvent): number {
    return event.id;
  }

  operationOptionTemplate = (option: any) => {
    return `<div class="flex align-items-center gap-2">
              <i class="${option.icon} text-sm"></i>
              <span>${option.label}</span>
            </div>`;
  };

  selectedOperationTemplate = (option: any, props: any) => {
    if (option) {
      return `<div class="flex align-items-center gap-2">
                <i class="${option.icon} text-sm"></i>
                <span>${option.label}</span>
              </div>`;
    }
    return `<span>${props.placeholder}</span>`;
  };

  /**
   * Check if a field name matches staff-related patterns
   * Patterns: *_updated_by, *_updatedby, *_created_by, clt_rep_id, etc.
   */
  isStaffField(fieldName: string): boolean {
    return StaffFieldMatcher.isStaffField(fieldName);
  }

  /**
   * Extract staff ID from event based on field pattern matching
   */
  getStaffIdFromEvent(event: any): number | null {
    return StaffFieldMatcher.getStaffIdFromEvent(event);
  }

  getNumberValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    } else if (!isNaN(Number(value))) {
      return Number(value);
    } else {
      return null;
    }
  }

  /**
   * Check if event is a client creation event
   */
  isClientCreateEvent(event: any): boolean {
    return event.entity_type === 'clients' && event.operation === 'CREATE';
  }

  /**
   * Check if we should hide the field display (for client creation with all-fields)
   */
  shouldHideFieldDisplay(event: any): boolean {
    return this.isClientCreateEvent(event) && event.fields === 'all-fields';
  }

  /**
   * Get display name for client events
   */
  getClientDisplayName(event: any): string {
    // Use entity_primary_name if available, otherwise fall back to entity_id
    return event.entity_primary_name || `Client #${event.entity_id}`;
  }

  /**
   * Handle client profile click - opens client profile sidebar
   */
  onClientProfileClick(event: any): void {
    if (this.isClientCreateEvent(event)) {
      // This should emit an event or call a service to open the client profile
      console.log('Opening client profile for:', {
        clientId: event.entity_id,
        clientName: this.getClientDisplayName(event),
        event: event,
      });

      // Example implementation - you can replace this with your actual sidebar service
      // this.clientProfileService.openClientProfile(event.entity_id);
      // or emit an event to parent component
      // this.clientProfileRequested.emit({ clientId: event.entity_id, clientName: this.getClientDisplayName(event) });
    }
  }

  /**
   * Check if we should show enum status values
   */
  shouldShowEnumStatus(event: any): boolean {
    return StatusEnumUtil.shouldShowEnumStatus(event);
  }

  /**
   * Get enum status label based on event type
   */
  getEnumStatusLabel(event: any, value: any): string {
    return StatusEnumUtil.getEnumStatusLabel(event, value);
  }

  /**
   * Get status style class for styling
   */
  getStatusStyleClass(event: any, value: any): string {
    return StatusEnumUtil.getStatusStyleClass(event, value);
  }

  /**
   * Get status icon
   */
  getStatusIcon(event: any, value: any): string {
    return StatusEnumUtil.getStatusIcon(event, value);
  }
}
